import { types } from 'sharetribe-flex-sdk';
import { isValoradorEnabled, valoradorMarketplaceId } from '../../util/valoradorEnvironment';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { hasPermissionToPostListings, isUserAuthorized } from '../../util/userHelpers';
import { listingPublicationErrors } from '../../util/listingPublication';
import { handoffPublicData, readHandoff, writeHandoff } from '../../util/valoradorHandoff';
import { queryAllOwnListings } from '../ManageListingsPage/manageListingGroups';
import { requestPublishListingDraft } from '../EditListingPage/EditListingPage.duck';

// Only explicit API rejection responses prove that creation did not succeed. A timeout,
// transport error or server failure remains ambiguous, even when a later query is empty.
const isRejectedCreation = error =>
  [400, 401, 403, 404, 422, 429].includes(error?.status) &&
  Array.isArray(error?.data?.errors) &&
  error.data.errors.length > 0 &&
  error.data.errors.every(item => Number(item.status) === error.status && item.code);

export const loadData = () => dispatch =>
  dispatch(fetchCurrentUser({ updateNotifications: false, enforce: true }));

/** Revalidate environment and account at the moment of writing, never from cached UI state. */
export const publishPrepared = ({ requestId, config }) => async (dispatch, getState, sdk) => {
  if (!isValoradorEnabled() || !navigator.locks) throw new Error('environment');
  return navigator.locks.request('uroborox-valorador-publication', async () => {
    const marketplace = await sdk.marketplace.show();
    if (marketplace.data.data.id.uuid !== valoradorMarketplaceId()) throw new Error('environment');
    await dispatch(loadData());
    const user = getState().user.currentUser;
    if (!isUserAuthorized(user) || !hasPermissionToPostListings(user))
      throw new Error('permission');
    let record = readHandoff();
    if (!record || record.requestId !== requestId) throw new Error('changed');
    if (record.ownerId && record.ownerId !== user.id.uuid) throw new Error('owner');
    record = writeHandoff({ ...record, ownerId: user.id.uuid });
    const data = handoffPublicData(record.publicData);
    const type = config.listing.listingTypes.find(t => t.listingType === data.listingType);
    if (!type?.transactionType?.alias || !type?.transactionType?.unitType)
      throw new Error('configuration');
    const profile = user.attributes.profile.publicData?.userType;
    const publicData = {
      ...data,
      transactionProcessAlias: type.transactionType.alias,
      unitType: type.transactionType.unitType,
      cardStyle: 'white',
      ...(data.listingType === 'mandato-compra' && profile ? { perfil_comprador: profile } : {}),
    };
    const payload = {
      title: record.title,
      description: '',
      publicData,
      privateData: { valoradorRequestId: record.requestId },
    };
    if (listingPublicationErrors({ attributes: payload }).length) throw new Error('invalid');
    // A lost create response must be reconciled, never blindly repeated.
    if (record.attempted && !record.listingId) {
      const response = await queryAllOwnListings(sdk, {});
      const matches = response.data.data.filter(
        l => l.attributes.privateData?.valoradorRequestId === record.requestId
      );
      if (matches.length !== 1) throw new Error('uncertain');
      record = writeHandoff({ ...record, listingId: matches[0].id.uuid });
    }
    if (!record.listingId) {
      record = writeHandoff({ ...record, attempted: true });
      let response;
      try {
        response = await sdk.ownListings.createDraft(payload, { expand: true });
      } catch (error) {
        if (isRejectedCreation(error)) {
          // Restore editing/retry only after a definitive rejection, under the publication lock.
          writeHandoff({ ...record, attempted: false });
          throw new Error('rejected');
        }
        throw error;
      }
      record = writeHandoff({
        ...record,
        listingId: response.data.data.id.uuid,
      });
    }
    const id = new types.UUID(record.listingId);
    let listing = (await sdk.ownListings.show({ id })).data.data;
    if (listing.attributes.privateData?.valoradorRequestId !== record.requestId)
      throw new Error('changed');
    if (listing.attributes.state === 'draft') {
      // The review applies to this exact prepared advertisement, not an externally edited draft.
      if (
        listing.attributes.title !== payload.title ||
        Object.keys(publicData).some(
          key =>
            JSON.stringify(listing.attributes.publicData[key]) !== JSON.stringify(publicData[key])
        )
      )
        throw new Error('changed');
      await dispatch(requestPublishListingDraft(id));
      listing = (await sdk.ownListings.show({ id })).data.data;
    }
    if (!['published', 'pendingApproval'].includes(listing.attributes.state))
      throw new Error('changed');
    writeHandoff({ ...record, completed: true });
    return listing.attributes.state;
  });
};
